const LAST_PLAYED_STORAGE_KEY = "m360.leafFall.lastAutoPlayedAt";
const AUTO_PLAY_INTERVAL_MS = 30 * 60 * 1000;
const SHARED_DECISION_WINDOW_MS = 5 * 1000;

type Decision = { due: boolean; decidedAt: number };

let lastDecision: Decision | undefined;

/**
 * The leaves fall on their own at most once every 30 minutes per browser, so they stay a treat instead of
 * replaying on every page load. Logos that mount together (the two header copies, for example) share one decision;
 * a logo that mounts later in the same page load, such as after a resize, checks the stored time again.
 */
export const shouldAutoPlay = (): boolean => {
  const now = Date.now();
  if (lastDecision && now - lastDecision.decidedAt < SHARED_DECISION_WINDOW_MS) {
    return lastDecision.due;
  }

  try {
    const lastPlayedAt = Number(window.localStorage.getItem(LAST_PLAYED_STORAGE_KEY));
    const elapsed = now - lastPlayedAt;
    const due = !(elapsed >= 0 && elapsed < AUTO_PLAY_INTERVAL_MS);
    if (due) {
      window.localStorage.setItem(LAST_PLAYED_STORAGE_KEY, String(now));
    }
    lastDecision = { due, decidedAt: now };
  } catch {
    lastDecision = { due: false, decidedAt: now };
  }

  return lastDecision.due;
};

export const resetAutoPlayDecision = () => {
  lastDecision = undefined;
};

/** Forgets the last auto-play so the next mount plays again. For demos such as the Storybook replay button. */
export const clearAutoPlayHistory = () => {
  resetAutoPlayDecision();
  try {
    window.localStorage.removeItem(LAST_PLAYED_STORAGE_KEY);
  } catch {
    // Storage is unavailable, so there is no history to clear.
  }
};
